import React, { useState, useEffect } from "react";
import { Panel, DetailRow, SortableHeader, useClientSort } from "../AdminUI";
import { adminService } from "../admin.service";
import { AdminSalesRep, AdminReferralSignup } from "../admin-types";

function InlineEditor({ value, onChange, onSave, onCancel, min, max }: {
    value: string;
    onChange: (v: string) => void;
    onSave: () => void;
    onCancel: () => void;
    min?: number;
    max?: number;
}) {
    return (
        <div className="flex items-center" style={{ gap: '0.25rem', display: 'flex', height: '2rem' }}>
            <input
                type="number"
                min={min}
                max={max}
                value={value}
                onChange={e => onChange(e.target.value)}
                className="input-text h-8 text-black"
                style={{ width: '4rem' }}
                autoFocus
            />
            <button onClick={onSave} className="action-btn solid small h-8 leading-none">Guardar</button>
            <button onClick={onCancel} className="action-btn ghost small h-8 leading-none">✕</button>
        </div>
    );
}

interface SalesRepDetailsProps {
    rep: AdminSalesRep;
    refreshKey?: number;
    onGoBack?: () => void;
    onUpdate?: () => void;
}

export default function SalesRepDetails({ rep, refreshKey, onGoBack, onUpdate }: SalesRepDetailsProps) {
    const [isGenerating, setIsGenerating] = useState(false);
    const [newCode, setNewCode] = useState("");
    const [newDiscount, setNewDiscount] = useState("0");
    const [newBonusMonths, setNewBonusMonths] = useState("0");
    const [newBonusMatches, setNewBonusMatches] = useState("0");

    // Send QR State
    const [sendingId, setSendingId] = useState<number | null>(null);

    // Discount editing state: maps code.id → draft discount value
    const [editingDiscountId, setEditingDiscountId] = useState<number | null>(null);
    const [discountDraft, setDiscountDraft] = useState<string>("");

    // Bonus months editing state
    const [editingMonthsId, setEditingMonthsId] = useState<number | null>(null);
    const [monthsDraft, setMonthsDraft] = useState<string>("");

    // Bonus matches editing state
    const [editingMatchesId, setEditingMatchesId] = useState<number | null>(null);
    const [matchesDraft, setMatchesDraft] = useState<string>("");

    // Signups
    const [signups, setSignups] = useState<AdminReferralSignup[]>([]);
    const codesSort = useClientSort(rep.referral_codes || []);
    const signupsSort = useClientSort(signups);
    const [loadingSignups, setLoadingSignups] = useState(false);

    useEffect(() => {
        setLoadingSignups(true);
        adminService.fetchSalesRepSignups(rep.id)
            .then(setSignups)
            .catch(console.error)
            .finally(() => setLoadingSignups(false));
    }, [rep.id, refreshKey]);

    const handleCreateCode = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newCode) return;
        try {
            await adminService.createReferralCode(rep.id, {
                code: newCode,
                discount_percent: parseInt(newDiscount, 10) || 0,
                bonus_months: parseInt(newBonusMonths, 10) || 0,
                bonus_matches: parseInt(newBonusMatches, 10) || 0,
            });
            setNewCode("");
            setNewDiscount("0");
            setNewBonusMonths("0");
            setNewBonusMatches("0");
            setIsGenerating(false);
            if (onUpdate) {
                onUpdate();
            } else {
                alert("Code created! Please refresh to see it.");
            }
        } catch (error) {
            console.error(error);
            alert("Error creating code");
        }
    }

    const handleEditDiscount = (codeId: number, currentDiscount: number) => {
        setEditingDiscountId(codeId);
        setDiscountDraft(String(currentDiscount));
    };

    const handleSaveDiscount = async (codeId: number) => {
        const value = parseInt(discountDraft, 10);
        if (isNaN(value) || value < 0 || value > 100) {
            alert("El descuento debe ser un número entre 0 y 100");
            return;
        }
        try {
            await adminService.updateReferralCode(codeId, { discount_percent: value });
            setEditingDiscountId(null);
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error(error);
            alert("Error al guardar el descuento");
        }
    };

    const handleSaveMonths = async (codeId: number) => {
        const months = parseInt(monthsDraft, 10);
        if (isNaN(months) || months < 0) {
            alert("Los meses deben ser un número positivo");
            return;
        }
        try {
            await adminService.updateReferralCode(codeId, { bonus_months: months });
            setEditingMonthsId(null);
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error(error);
            alert("Error al guardar los meses");
        }
    };

    const handleSaveMatches = async (codeId: number) => {
        const matches = parseInt(matchesDraft, 10);
        if (isNaN(matches) || matches < 0) {
            alert("Los matches deben ser un número positivo");
            return;
        }
        try {
            await adminService.updateReferralCode(codeId, { bonus_matches: matches });
            setEditingMatchesId(null);
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error(error);
            alert("Error al guardar los matches");
        }
    };

    const handleSendQR = async (codeId: number) => {
        setSendingId(codeId);
        try {
            await adminService.sendQrCode(codeId);
            alert("QR Code sent to email!");
        } catch (error) {
            console.error(error);
            alert("Error sending email");
        } finally {
            setSendingId(null);
        }
    }

    return (
        <div className="col-span-12">
             <div className="mb-4">
                <button onClick={onGoBack} className="action-btn ghost">⬅ Volver a Lista</button>
            </div>

            <Panel title={`Detalle: ${rep.name}`}>
                <div className="grid grid-cols-2 gap-4 mb-6">
                    <DetailRow label="Nombre" value={rep.name} />
                    <DetailRow label="Email" value={rep.email} />
                    <DetailRow label="Creado" value={new Date(rep.created_at).toLocaleDateString()} />
                    <DetailRow label="Total Referidos" value={rep.referral_count} />
                </div>

                <div className="detail-section">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="detail-section-title">Códigos de Referido</h3>
                        <button 
                            onClick={() => setIsGenerating(!isGenerating)}
                            className="action-btn solid small"
                        >
                            {isGenerating ? "Cancelar" : "+ Generar Código"}
                        </button>
                    </div>

                    {isGenerating && (
                        <div className="admin-form-panel">
                             <form onSubmit={handleCreateCode} className="admin-form">
                                <div className="form-group">
                                    <label>Código (ej. ANTONIO10)</label>
                                    <input
                                        type="text"
                                        value={newCode}
                                        onChange={e => setNewCode(e.target.value)}
                                        className="input-text"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Descuento (%)</label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={newDiscount}
                                        onChange={e => setNewDiscount(e.target.value)}
                                        className="input-text w-24"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Meses Extra</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={newBonusMonths}
                                        onChange={e => setNewBonusMonths(e.target.value)}
                                        className="input-text w-24"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Matches Extra</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={newBonusMatches}
                                        onChange={e => setNewBonusMatches(e.target.value)}
                                        className="input-text w-24"
                                    />
                                </div>
                                <button type="submit" className="action-btn solid">Guardar</button>
                             </form>
                        </div>
                    )}

                    <table className="table w-full">
                        <thead className="text-left bg-slate-50">
                            <tr>
                                <SortableHeader label="Código" sortKey="code" sortConfig={codesSort.sortConfig} onSort={codesSort.onSort} className="px-3 py-2" />
                                <SortableHeader label="Activo" sortKey="is_active" sortConfig={codesSort.sortConfig} onSort={codesSort.onSort} className="px-3 py-2" />
                                <SortableHeader label="Descuento (%)" sortKey="discount_percent" sortConfig={codesSort.sortConfig} onSort={codesSort.onSort} className="px-3 py-2" />
                                <SortableHeader label="Meses de Prueba Gratis" sortKey="bonus_months" sortConfig={codesSort.sortConfig} onSort={codesSort.onSort} className="px-3 py-2" />
                                <SortableHeader label="Matches de Bonificación" sortKey="bonus_matches" sortConfig={codesSort.sortConfig} onSort={codesSort.onSort} className="px-3 py-2" />
                                <SortableHeader label="Registros" sortKey="signup_count" sortConfig={codesSort.sortConfig} onSort={codesSort.onSort} className="px-3 py-2" />
                                <th className="px-3 py-2">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {codesSort.sorted.map(code => (
                                <tr key={code.id}>
                                    <td className="px-3 py-2 font-bold">{code.code}</td>
                                    <td className="px-3 py-2">
                                        {code.is_active ? <span className="bool-yes">SI</span> : <span className="bool-no">NO</span>}
                                    </td>
                                    <td className="px-3 py-2">
                                        {editingDiscountId === code.id ? (
                                            <InlineEditor
                                                value={discountDraft}
                                                onChange={setDiscountDraft}
                                                onSave={() => handleSaveDiscount(code.id)}
                                                onCancel={() => setEditingDiscountId(null)}
                                                min={0}
                                                max={100}
                                            />
                                        ) : (
                                            <span
                                                className="cursor-pointer hover:underline"
                                                onClick={() => handleEditDiscount(code.id, code.discount_percent)}
                                                title="Click para editar"
                                            >
                                                {code.discount_percent > 0
                                                    ? <span className="bool-yes">{code.discount_percent}%</span>
                                                    : <span className="text-slate-400">0%</span>
                                                }
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        {editingMonthsId === code.id ? (
                                            <InlineEditor
                                                value={monthsDraft}
                                                onChange={setMonthsDraft}
                                                onSave={() => handleSaveMonths(code.id)}
                                                onCancel={() => setEditingMonthsId(null)}
                                                min={0}
                                            />
                                        ) : (
                                            <span
                                                className="cursor-pointer hover:underline"
                                                onClick={() => { setEditingMonthsId(code.id); setMonthsDraft(String(code.bonus_months ?? 0)); }}
                                                title="Click para editar"
                                            >
                                                {(code.bonus_months ?? 0) > 0
                                                    ? <span className="bool-yes">+{code.bonus_months} mes{code.bonus_months !== 1 ? "es" : ""}</span>
                                                    : <span className="text-slate-400">—</span>
                                                }
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        {editingMatchesId === code.id ? (
                                            <InlineEditor
                                                value={matchesDraft}
                                                onChange={setMatchesDraft}
                                                onSave={() => handleSaveMatches(code.id)}
                                                onCancel={() => setEditingMatchesId(null)}
                                                min={0}
                                            />
                                        ) : (
                                            <span
                                                className="cursor-pointer hover:underline"
                                                onClick={() => { setEditingMatchesId(code.id); setMatchesDraft(String(code.bonus_matches ?? 0)); }}
                                                title="Click para editar"
                                            >
                                                {(code.bonus_matches ?? 0) > 0
                                                    ? <span className="bool-yes">+{code.bonus_matches}</span>
                                                    : <span className="text-slate-400">—</span>
                                                }
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">{code.signup_count ?? 0}</td>
                                    <td className="px-3 py-2">
                                        <button
                                            onClick={() => handleSendQR(code.id)}
                                            disabled={sendingId === code.id}
                                            className="action-btn outline small"
                                        >
                                            {sendingId === code.id ? "Enviando..." : "Enviar QR por Email"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {(!rep.referral_codes || rep.referral_codes.length === 0) && (
                                <tr>
                                    <td colSpan={7} className="px-3 py-4 text-center text-slate-400">
                                        Sin códigos generados
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="detail-section mt-6">
                    <h3 className="detail-section-title mb-4">Empresas Registradas</h3>
                    {loadingSignups ? (
                        <div className="p-4 text-center text-slate-400">Cargando...</div>
                    ) : (
                        <table className="table w-full">
                            <thead className="text-left bg-slate-50">
                                <tr>
                                    <SortableHeader label="Empresa" sortKey="company_name" sortConfig={signupsSort.sortConfig} onSort={signupsSort.onSort} className="px-3 py-2" />
                                    <SortableHeader label="Email" sortKey="email" sortConfig={signupsSort.sortConfig} onSort={signupsSort.onSort} className="px-3 py-2" />
                                    <SortableHeader label="Código Usado" sortKey="referral_code" sortConfig={signupsSort.sortConfig} onSort={signupsSort.onSort} className="px-3 py-2" />
                                    <SortableHeader label="Fecha Registro" sortKey="signed_up_at" sortConfig={signupsSort.sortConfig} onSort={signupsSort.onSort} className="px-3 py-2" />
                                </tr>
                            </thead>
                            <tbody>
                                {signupsSort.sorted.map(s => (
                                    <tr key={s.company_id}>
                                        <td className="px-3 py-2 font-bold">{s.company_name}</td>
                                        <td className="px-3 py-2">{s.email}</td>
                                        <td className="px-3 py-2">
                                            <span className="bool-yes">{s.referral_code}</span>
                                        </td>
                                        <td className="px-3 py-2">{new Date(s.signed_up_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                                {signups.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="px-3 py-4 text-center text-slate-400">
                                            Sin registros todavía
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </Panel>
        </div>
    );
}
